import { ninetyDeg, twoSeventyDeg } from '@baseValues'
import { Acabados, Hole, Item, LinkedItem, Types } from '@types'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { Box3, Matrix4 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import getMorphsFromGLB from '../../../store/getMorphsFromGLB'
import { getZonesFromGLB } from '../getZonesFromGLB'
import {
  activateSyntheticEncimeraFootprintMorphs,
  applyFreeDimensionUniversalStyleMorphs,
  resolveFreeDimensionAnchoViaGlbMorph,
  resolveFreeDimensionAltoViaGlbMorph,
  resolveFreeDimensionFondoViaGlbMorph,
  getFreeDimensionAltoOffsetMm,
  getFreeDimensionScaleRatios,
  getFreeDimensionCatalogAnchoMm,
  getFreeDimensionPlacementStoreAltoMm,
  getFreeDimensionStoreAltoAtPlace,
  logFreeDimensionAncho,
  logFreeDimensionDrawLA,
  logFreeDimensionPlacement,
  resolveFreeDimensionPlacementAnchoMm,
  resolveFreeDimensionScaleBaseMm,
  clampMorphMm,
  isFreeDimensionAltoApiMorphCatalog,
  isFreeDimensionAnchoApiMorphCatalog,
  itemUsesFreeDimensionAltoApiMorph,
  morphHasValidRange,
  isFreeDimensionLateralOrCostadoCatalog,
  shouldUseIndependentZocaloUnit,
  usesFreeDimensionScaling,
  usesPureGroupFreeDimensionScale,
  vector3ToFreeDimensionGlbBaseMm,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import {
  buildDimFlowSnapshot,
  logDimFlow,
} from '@utils/debug/dimensionFlowDiagnostic'
import { getGLBSize, getGLBSizeForFreeDimension } from './getGLBSize'
import { normalizeMirroredGlbScene } from '../normalizeMirroredGlbScene'

type Props = {
  id: string
  glbData: any
  type: Types
  catalogID: string
  visible?: boolean
  icono?: string
  matrix?: Matrix4 | undefined | null
  backGLBInfo: import('../../../types').BackGLBInfo
  extraInfo: { zonas: { idAcabadoUnidad: number; zona: string }[] }
  img?: string
  shouldGoToBackHeight?: boolean
  itemInfo: Record<string, unknown>
}

export function newGetDataFromGLB({
  glbData,
  icono,
  catalogID,
  visible,
  matrix,
  id,
  type,
  backGLBInfo,
  extraInfo,
  img,
  itemInfo,
  shouldGoToBackHeight,
  ...rest
}: Props) {
  const {
    idUnidades,
    referenciaBlender,
    descripcion,
    descripcionLarga,
    ancho,
    fondo,
    alto,
    mano,
    altura,
    tipoNormalizado,
    tipoUnidNorm,
    universal,
    precio,
    acabados,
    referenciaJpgpng,
    // campos personalizados no están en el tipo, se accede por string
  } = backGLBInfo

  const armazonBlancoFromBackend = (backGLBInfo as any)['armazonBlanco']
  const bisagra = (backGLBInfo as any)['bisagra']
  const idCambioArmazon = (backGLBInfo as any)['idCambioArmazon']
  const idCambioGolaBlanco = (backGLBInfo as any)['idCambioGolaBlanco']
  const idCambioGolaPetroleo = (backGLBInfo as any)['idCambioGolaPetroleo']
  const idCambioRutik = (backGLBInfo as any)['idCambioRutik']
  const idCambioRutik2 = (backGLBInfo as any)['idCambioRutik2']
  const idCambioLineal = (backGLBInfo as any)['idCambioLineal']
  const idCambioLara = (backGLBInfo as any)['idCambioLara']
  const url = (backGLBInfo as any)['url']
  const paginaPdf = (backGLBInfo as any)['paginaPdf']
  const referencia = (backGLBInfo as any)['referencia']

  // Frontales: drawFrontalsFromSnappedGroups ya pone Y = tope visual MBJ + encimera
  // (incluye zócalo independiente). No pisar con altura de catálogo del Frontal.
  const isFrontalCatalog =
    String(catalogID ?? '').toUpperCase() === 'FRONTAL' ||
    /frontal/i.test(String(descripcion ?? ''))

  let preservedFrontalY: number | null = null
  if (matrix && isFrontalCatalog) {
    const frontalPos = toVector3()
    matrix.decompose(frontalPos, toQuaternion(), toVector3())
    preservedFrontalY = frontalPos.y
  }

  // Determinar ArmazonBlanco: priorizar el valor del backend, sino sincronizar con catalogID
  // Si el catalogID tiene prefijo "PE " → es petróleo (false), sino es blanco (true)
  let armazonBlanco: boolean
  if (typeof armazonBlancoFromBackend === 'boolean') {
    // Si viene del backend como booleano válido, usarlo
    armazonBlanco = armazonBlancoFromBackend
  } else {
    // Si no viene del backend o es undefined/null, sincronizar con catalogID
    const catalogIDStr = String(catalogID || '')
    armazonBlanco = !catalogIDStr.startsWith('PE ')
  }

  const { zones, textures } = getZonesFromGLB(
    glbData.scene?.children,
    acabados as Acabados[],
    extraInfo,
  ) || {
    zones: {},
    textures: [],
  }

  const newPosition = toVector3()
  if (matrix) {
    matrix.decompose(newPosition, toQuaternion(), toVector3())

    // const matrixY = newPosition.y
    // Si altura es null, mantener null para poder detectarlo (no convertir a 0)
    const backendAltura = altura !== null && altura !== undefined ? altura : null

    // console.log('🔍 [newGetDataFromGLB] Valores al dibujar:', {
    //   catalogID,
    //   tipoNormalizado,
    //   shouldGoToBackHeight,
    //   matrixY: matrixY.toFixed(2),
    //   backendAltura: backendAltura !== null ? backendAltura.toFixed(2) : 'null',
    //   alto: alto?.toFixed(2),
    //   alturaRaw: altura,
    //   diferencia: backendAltura !== null ? Math.abs(matrixY - backendAltura).toFixed(2) : 'N/A',
    // })

    if (!shouldGoToBackHeight) {
      // IMPORTANTE: La altura de colocación (y) SIEMPRE debe venir del backend del nuevo item
      // No debe usar la posición Y calculada de la matriz que viene del item de referencia
      //
      // Diferencia clave:
      // - alto: tamaño de la unidad (800) -> se usa para height del item (SIEMPRE del nuevo item)
      // - altura: altura de colocación (1500) -> se usa para y (posición Y del item) - SIEMPRE del backend
      //
      // Excepción: Frontal — Y ya viene calculada desde el alto real de los MBJ del grupo.
      //
      // Reglas:
      // 1. Si altura tiene valor, usar ese valor
      // 2. Si altura es null y es MBJ, usar 0 (no la posición Y de la matriz)
      // 3. Si altura es null y no es MBJ, usar 0 como fallback
      //
      // La posición X y Z de la matriz se preservan (vienen de getRelativeMatrix para posicionamiento relativo)
      // Pero la posición Y (altura de colocación) siempre debe ser la del backend del nuevo item

      let finalY: number

      if (preservedFrontalY != null) {
        finalY = preservedFrontalY
      } else if (backendAltura !== null && backendAltura > 0) {
        // Caso 1: Usar altura del backend si tiene valor
        finalY = backendAltura
      } else if (tipoNormalizado === 'MBJ') {
        // Caso 2: Si es MBJ y altura es null, usar 0 (no la posición Y de la matriz)
        finalY = 0
      } else {
        // Caso 3: Para otros tipos si altura es null, usar 0 como fallback
        finalY = 0
      }

      matrix = matrix.setPosition(newPosition.x, finalY, newPosition.z)
    } else {
      // Si shouldGoToBackHeight es true, usar altura del backend (o 0 si es null y MBJ)
      const finalY =
        preservedFrontalY != null
          ? preservedFrontalY
          : backendAltura !== null && backendAltura > 0
            ? backendAltura
            : tipoNormalizado === 'MBJ'
              ? 0
              : 0
      matrix = matrix.setPosition(newPosition.x, finalY, newPosition.z)
    }
  }

  const glbScene = glbData?.scene ?? glbData
  // *I con scale.x < 0: alinear AABB a +X antes de medir OBB/glbSize (idempotente).
  normalizeMirroredGlbScene(glbScene)
  const glbBox3 = new Box3().setFromObject(glbScene)
  const boundingBox = new OBB().fromBox3(glbBox3)
  const fullGlbSize = getGLBSize(glbScene)
  const catalogIDForFreeDim = catalogID ?? (backGLBInfo as { codigo?: string })?.codigo
  const useFreeDimScale = usesFreeDimensionScaling({
    catalogID: catalogIDForFreeDim,
    hasMorphs: Boolean((backGLBInfo as { morphs?: unknown }).morphs),
    tipoNormalizado: tipoNormalizado || '',
    description: descripcion || '',
    referencia: referencia ?? '',
  })
  const useIndependentZocalo = shouldUseIndependentZocaloUnit({
    catalogID: catalogIDForFreeDim,
    tipoNormalizado: tipoNormalizado || '',
    description: descripcion || '',
    referencia: referencia ?? '',
  })
  // Dimensiones libres: bbox de escala.
  // - MBJ/COL + zócalo hijo: excluir losa Zocalo si el GLB aún la trae.
  // - Laterales LA/LB/… (p.ej. LA8035): medir zona Costado. Si se usa el AABB
  //   completo (~900 con zócalo) con height 800 → scaleY 800/900 y el panel
  //   se ve ~711 mm aunque el sidebar diga 800.
  const useCostadoAwareGlb =
    useFreeDimScale &&
    (useIndependentZocalo ||
      isFreeDimensionLateralOrCostadoCatalog({ catalogID: catalogIDForFreeDim }))
  const glbSize = useCostadoAwareGlb
    ? getGLBSizeForFreeDimension(
        glbScene as import('three').Object3D,
        fullGlbSize,
        acabados as Acabados[],
        catalogIDForFreeDim,
      )
    : fullGlbSize


  // Función para obtener el height correcto basado en el acabado de la textura
  const getHeightFromTexture = (textures: any[], defaultHeight: number) => {
    if (descripcion?.toLowerCase().includes('encimera')) {
      const encimeraTexture = textures.find(
        (t) => t.zone === 'Encimera' || t.zoneDisplayName === 'Encimera',
      )
      if (encimeraTexture?.acabado) {
        const acabado = encimeraTexture.acabado.toLowerCase()
        if (acabado.includes('porcelanicas 12')) return 12
        if (acabado.includes('porcelanicas 20')) return 20
        if (acabado.includes('postformadas')) return 30
        if (acabado.includes('compacto')) return 10
      }
    }
    return defaultHeight
  }

  const correctHeight = getHeightFromTexture(textures, alto ?? 0)

  // IMPORTANTE: La altura de colocación (y) SIEMPRE debe venir del backend del nuevo item
  // No debe usar la posición Y de la matriz que podría venir del item de referencia
  //
  // Reglas para finalY:
  // 1. Frontal: conservar Y calculada desde alto real de MBJ (+ encimera)
  // 2. Si altura tiene valor, usar ese valor
  // 3. Si altura es null y es MBJ, usar 0 (no la posición Y de la matriz)
  // 4. Si altura es null y no es MBJ, usar 0 como fallback
  const finalY =
    preservedFrontalY != null
      ? preservedFrontalY
      : altura !== null && altura !== undefined
        ? altura
        : tipoNormalizado === 'MBJ'
          ? 0
          : 0
  const finalHeight = correctHeight

  // Actualizar la matriz para usar la altura correcta en Y (ya se actualizó arriba, pero asegurémonos)
  if (matrix) {
    const currentPos = toVector3()
    matrix.decompose(currentPos, toQuaternion(), toVector3())
    matrix = matrix.setPosition(currentPos.x, finalY, currentPos.z)
  }

  // console.log('🔍 [newGetDataFromGLB] Propiedades finales:', {
  //   catalogID,
  //   tipoNormalizado,
  //   finalY: finalY.toFixed(2),
  //   finalHeight: finalHeight.toFixed(2),
  //   alto: alto?.toFixed(2),
  //   altura: altura?.toFixed(2),
  //   matrixY: matrix ? newPosition.y.toFixed(2) : 'sin matriz',
  // })

  const parsedMorphs = (() => {
    const morphs = getMorphsFromGLB(backGLBInfo)
    // Si es encimera, actualizar el morph "Alto" con el height correcto
    if (descripcion?.toLowerCase().includes('encimera')) {
      const withAlto = morphs.map((morph) => {
        if (morph.name === 'Alto') {
          return {
            ...morph,
            default: correctHeight,
            _default: correctHeight,
            value: correctHeight,
          }
        }
        return morph
      })
      // API morphs=null → sintéticos min===max: sin esto Ancho/Fondo quedan en rest
      // (Encimera b30 200×520) y la losa se ve pequeña sobre el módulo.
      return activateSyntheticEncimeraFootprintMorphs(withAlto)
    }
    return morphs
  })()

  const anchoMorphDefault = parsedMorphs.find((morph) => morph.name === 'Ancho')?.default
  const fondoMorphDefault = parsedMorphs.find((morph) => morph.name === 'Fondo')?.default
  const altoMorphDefault = parsedMorphs.find((morph) => morph.name === 'Alto')?.default

  const freeDimensionGlbBaseMmEarly = useFreeDimScale
    ? vector3ToFreeDimensionGlbBaseMm(glbSize)
    : undefined

  const layoutWidthMm = useFreeDimScale
    ? resolveFreeDimensionPlacementAnchoMm(
        backGLBInfo as Parameters<typeof resolveFreeDimensionPlacementAnchoMm>[0],
        freeDimensionGlbBaseMmEarly,
        catalogIDForFreeDim,
      )
    : (anchoMorphDefault ?? ancho ?? 0)

  const catalogAnchoMm = useFreeDimScale
    ? getFreeDimensionCatalogAnchoMm(backGLBInfo as { ancho?: number }, layoutWidthMm)
    : layoutWidthMm

  const width0 = layoutWidthMm

  const catalogBodyHeight = useFreeDimScale
    ? (alto ?? altoMorphDefault ?? finalHeight)
    : (altoMorphDefault ?? finalHeight)
  const thickness0 = fondoMorphDefault ?? fondo ?? 0

  const altoMorphParsed = parsedMorphs.find((m) => m.name === 'Alto')
  const anchoMorphParsed = parsedMorphs.find((m) => m.name === 'Ancho')
  const encimeraAltoApiRange =
    useFreeDimScale &&
    isFreeDimensionAltoApiMorphCatalog({ catalogID: catalogIDForFreeDim }) &&
    morphHasValidRange(altoMorphParsed)
  const lateralCascadaAnchoApiRange =
    useFreeDimScale &&
    isFreeDimensionAnchoApiMorphCatalog({ catalogID: catalogIDForFreeDim }) &&
    morphHasValidRange(anchoMorphParsed)

  const height0 = useFreeDimScale
    ? encimeraAltoApiRange
      ? clampMorphMm(
          altoMorphDefault ?? correctHeight,
          altoMorphParsed?.min,
          altoMorphParsed?.max,
        )
      : getFreeDimensionPlacementStoreAltoMm(
          backGLBInfo as Parameters<typeof getFreeDimensionPlacementStoreAltoMm>[0],
          catalogBodyHeight,
          catalogIDForFreeDim,
          {
            tipoNormalizado: tipoNormalizado || '',
            description: descripcion || '',
            referencia: referencia ?? '',
            hasIndependentZocalo: useIndependentZocalo,
          },
        )
    : catalogBodyHeight

  const freeDimensionGlbBaseMm = freeDimensionGlbBaseMmEarly
  const freeDimensionCatalogMm = useFreeDimScale
    ? { x: catalogAnchoMm, y: catalogBodyHeight, z: thickness0 }
    : undefined
  const freeDimensionScaleBaseMm =
    useFreeDimScale && freeDimensionCatalogMm && freeDimensionGlbBaseMm
      ? resolveFreeDimensionScaleBaseMm(freeDimensionCatalogMm, freeDimensionGlbBaseMm)
      : undefined

  const altoOffsetMm = useFreeDimScale
    ? getFreeDimensionAltoOffsetMm({
        catalogID: catalogIDForFreeDim,
        tipoNormalizado: tipoNormalizado || '',
        description: descripcion || '',
        referencia: referencia ?? '',
        hasIndependentZocalo: useIndependentZocalo,
      })
    : 0

  const usePureGroupFreeDim = usesPureGroupFreeDimensionScale({
    catalogID: catalogIDForFreeDim,
    tipoNormalizado: tipoNormalizado || '',
    description: descripcion || '',
    referencia: referencia ?? '',
    hasIndependentZocalo: useIndependentZocalo,
  })

  // MBJ/COL/MAL (pure group): no viaGlbMorph — morph clásico + scale de grupo.
  const freeDimensionAnchoViaGlbMorph =
    useFreeDimScale &&
    !usePureGroupFreeDim &&
    !lateralCascadaAnchoApiRange &&
    freeDimensionGlbBaseMm &&
    freeDimensionCatalogMm
      ? resolveFreeDimensionAnchoViaGlbMorph(
          freeDimensionGlbBaseMm,
          freeDimensionCatalogMm,
          width0,
        )
      : false

  const freeDimensionAltoViaGlbMorph =
    useFreeDimScale &&
    !usePureGroupFreeDim &&
    freeDimensionGlbBaseMm &&
    freeDimensionCatalogMm
      ? resolveFreeDimensionAltoViaGlbMorph(
          freeDimensionGlbBaseMm,
          freeDimensionCatalogMm,
          altoOffsetMm,
        )
      : false

  const freeDimensionFondoViaGlbMorph =
    useFreeDimScale &&
    !usePureGroupFreeDim &&
    freeDimensionGlbBaseMm &&
    freeDimensionCatalogMm
      ? resolveFreeDimensionFondoViaGlbMorph(
          freeDimensionGlbBaseMm,
          freeDimensionCatalogMm,
          catalogIDForFreeDim,
        )
      : false

  let freeDimensionMorphs = parsedMorphs
  if (useFreeDimScale && freeDimensionGlbBaseMm && freeDimensionCatalogMm) {
    freeDimensionMorphs = applyFreeDimensionUniversalStyleMorphs(
      parsedMorphs,
      {
        width: width0,
        height: height0,
        thickness: thickness0,
      },
      freeDimensionGlbBaseMm,
      freeDimensionCatalogMm,
      freeDimensionAnchoViaGlbMorph,
      freeDimensionAltoViaGlbMorph,
      freeDimensionFondoViaGlbMorph,
      altoOffsetMm,
      encimeraAltoApiRange,
      lateralCascadaAnchoApiRange,
      useIndependentZocalo,
    )
  }

  if (useFreeDimScale) {
    logFreeDimensionPlacement('newGetDataFromGLB', catalogIDForFreeDim, {
      morphs: freeDimensionMorphs,
      width: width0,
      heightStore: height0,
      thickness: thickness0,
      catalogMm: freeDimensionCatalogMm,
      glbBaseMm: freeDimensionGlbBaseMm,
      altoOffsetMm,
    })
    logFreeDimensionAncho(
      'newGetDataFromGLB',
      {
        catalogID: catalogIDForFreeDim,
        width: width0,
        morphs: freeDimensionMorphs,
        hasMorphs: false,
        freeDimensionCatalogMm,
        freeDimensionGlbBaseMm,
        freeDimensionScaleBaseMm,
      },
      { layoutWidthMm, catalogAnchoMm, headerAncho: ancho },
    )
  }

  if (useFreeDimScale && freeDimensionCatalogMm && freeDimensionGlbBaseMm) {
    const drawItem = {
      catalogID: catalogIDForFreeDim,
      width: width0,
      height: height0,
      thickness: thickness0,
      freeDimensionCatalogMm,
      freeDimensionGlbBaseMm,
      freeDimensionScaleBaseMm,
      hasMorphs: false,
    }
    const ratios = getFreeDimensionScaleRatios(drawItem)
    const matrixY = matrix ? new Matrix4().copy(matrix).elements[13] : null
    const offsetMm = getFreeDimensionAltoOffsetMm({
      catalogID: catalogIDForFreeDim,
      tipoNormalizado: tipoNormalizado || '',
      description: descripcion || '',
      referencia: referencia ?? '',
      hasIndependentZocalo: useIndependentZocalo,
    })
    const bboxIncludesOffset =
      freeDimensionGlbBaseMm.y > freeDimensionCatalogMm.y + 1 &&
      freeDimensionGlbBaseMm.y >= freeDimensionCatalogMm.y + offsetMm * 0.85
    logFreeDimensionDrawLA('newGetDataFromGLB', catalogIDForFreeDim, {
      altoApi: alto,
      catalogBodyHeight,
      heightStore: height0,
      altoOffsetMm: offsetMm,
      glbBaseMm: freeDimensionGlbBaseMm,
      scaleBaseMm: freeDimensionScaleBaseMm,
      scaleRatios: ratios,
      scaleModeAlto: bboxIncludesOffset
        ? String(catalogIDForFreeDim).toUpperCase().startsWith('LA')
          ? '(glbY/cat)×(sidebar/cat)×correcciónInterpolada'
          : 'sidebar/scaleBaseY'
        : 'sidebar×(cat+offset)/(scaleBase×cat)',
      matrixY,
      alturaColocacion: altura,
      tipoNormalizado,
    })
  }

  // Diagnóstico DimFlow: nacimiento de dimensiones al crear unidad desde GLB+catálogo
  logDimFlow({
    file: 'utils/sceneObjects/items/newGetDataFromGLB.ts',
    fn: 'newGetDataFromGLB',
    phase: 'unit.create',
    reason:
      'Creación de item: catálogo (ancho/alto/fondo) + bbox GLB + morphs API → width/height/thickness + flags freeDimension*',
    snapshot: buildDimFlowSnapshot(
      {
        id,
        referencia,
        catalogID: catalogIDForFreeDim,
        tipoNormalizado: tipoNormalizado || '',
        description: descripcion || '',
        width: width0,
        height: height0,
        thickness: thickness0,
        hasMorphs: useFreeDimScale
          ? itemUsesFreeDimensionAltoApiMorph({
              catalogID: catalogIDForFreeDim,
              morphs: freeDimensionMorphs,
            })
          : Boolean((backGLBInfo as { morphs?: unknown }).morphs),
        morphs: freeDimensionMorphs,
        freeDimensionGlbBaseMm,
        freeDimensionCatalogMm,
        freeDimensionScaleBaseMm,
        freeDimensionAnchoViaGlbMorph,
        freeDimensionAltoViaGlbMorph,
        freeDimensionFondoViaGlbMorph,
        matrix: matrix ?? undefined,
        boundingBox,
      },
      {
        catalogHeaderMm: { ancho, alto, fondo },
        usesFreeDimension: useFreeDimScale,
        usesMorph: Boolean((backGLBInfo as { morphs?: unknown }).morphs) && !useFreeDimScale,
        usesScale: useFreeDimScale || !Boolean((backGLBInfo as { morphs?: unknown }).morphs),
        glbSizeMeters: { x: glbSize.x, y: glbSize.y, z: glbSize.z },
        note: useCostadoAwareGlb
          ? 'glbBaseMm = Costado / sin losa Zocalo (getGLBSizeForFreeDimension).'
          : 'glbBaseMm = bbox completo del GLB.',
      },
    ),
  })

  let props = {
    glbPath: referenciaBlender,
    idUnidades,
    type: type === 'linkedItem' ? ('linkedItem' as const) : type,
    id,
    universal,
    textures,
    snappedWall: null,
    description: descripcion || '',
    price: precio ?? 0,
    y: finalY, // SIEMPRE usar altura del backend del nuevo item
    tipoNormalizado: tipoNormalizado || '',
    tipoUnidNorm: tipoUnidNorm || '',
    image: icono,
    // IMPORTANT: usar el valor inicial real del morph (el.valor), no el campo ancho/fondo/alto de cabecera.
    // En RB805 el backend trae ancho=100 pero morph Ancho.valor=50, y al dibujar debe iniciar en 50.
    width: width0,
    height: height0,
    thickness: thickness0,
    zones,
    boundingBox,
    glbSize,
    catalogID,
    isBlocked: false,
    long: descripcionLarga || '',
    hand: mano || '',
    mano: 'D',
    referencia: referencia ?? '',
    selected: true,
    rotation: twoSeventyDeg,
    url: url ?? '',
    matrix:
      matrix ?? new Matrix4().makeRotationY(-ninetyDeg).setPosition(toVector3([0, altura ?? 0, 0])),
    visible: Boolean(visible),
    // MBJ/COL/MAL (pure group): morphs clásicos del API + scale si sidebar > max.
    // Resto free-dim: sin morphs de malla salvo encimeras con rango Alto API.
    hasMorphs: usesPureGroupFreeDimensionScale({
      catalogID: catalogIDForFreeDim,
      tipoNormalizado: tipoNormalizado || '',
      description: descripcion || '',
      referencia: referencia ?? '',
      hasIndependentZocalo: useIndependentZocalo,
    })
      ? Boolean((backGLBInfo as { morphs?: unknown }).morphs)
      : useFreeDimScale
        ? itemUsesFreeDimensionAltoApiMorph({
            catalogID: catalogIDForFreeDim,
            morphs: freeDimensionMorphs,
          })
        : Boolean((backGLBInfo as { morphs?: unknown }).morphs),
    morphs: freeDimensionMorphs,
    ...(freeDimensionGlbBaseMm ? { freeDimensionGlbBaseMm } : {}),
    ...(Number.isFinite(glbBox3.min.y)
      ? { freeDimensionGlbMinYMm: Math.round(glbBox3.min.y * 1000) }
      : {}),
    ...(freeDimensionCatalogMm ? { freeDimensionCatalogMm } : {}),
    ...(freeDimensionScaleBaseMm ? { freeDimensionScaleBaseMm } : {}),
    ...(useFreeDimScale
      ? {
          freeDimensionAnchoViaGlbMorph,
          freeDimensionAltoViaGlbMorph,
          freeDimensionFondoViaGlbMorph,
        }
      : {}),
    paginaPdf,
    hinge: bisagra,
    ArmazonBlanco: armazonBlanco, // <-- mapeo correcto
    idcambioarmazon: idCambioArmazon, // <-- mapeo correcto
    idcambiogolablanco: idCambioGolaBlanco, // <-- mapeo correcto
    idcambiogolapetroleo: idCambioGolaPetroleo, // <-- mapeo correcto
    idCambioRutik,
    idCambioRutik2,
    idCambioLineal,
    idCambioLara,
    regleta: backGLBInfo.regleta,
    regletaMaximo: backGLBInfo.regletaMaximo,
    regletaMinimo: backGLBInfo.regletaMinimo,
    ...rest,
    // Después de rest: no dejar que extras pisen el contrato de zócalo independiente.
    hasIndependentZocalo: useIndependentZocalo,
    ...(useIndependentZocalo ? { independentZocaloHeightMm: 100 } : {}),
  }
  if (type === 'linkedItem') {
    let bisagraBool = false
    if (typeof bisagra === 'boolean') {
      bisagraBool = bisagra
    } else if (typeof bisagra === 'string') {
      bisagraBool = bisagra.toLowerCase() === 'true'
    } else {
      bisagraBool = Boolean(bisagra)
    }
    ;(props as any).bisagra = bisagraBool
  }

  if (type === 'linkedItem') {
    ;(props as any).childs = new Set()
  }

  if (type === 'linkedSon' && /encimera/i.test(descripcion ?? '')) {
    const encX = typeof ancho === 'number' && ancho > 0 ? ancho : width0
    const encZ = typeof fondo === 'number' && fondo > 0 ? fondo : thickness0
    const encY = typeof alto === 'number' && alto > 0 ? alto : height0
    if (encX > 0 && encZ > 0) {
      ;(props as any).encimeraCatalogMm = { x: encX, y: encY, z: encZ }
    }
  }

  if (img) {
    props.image = img
  }

  if (itemInfo) {
    props = { ...props, ...itemInfo }
    if ('x' in itemInfo && typeof itemInfo.x === 'number') (props as any).x = itemInfo.x
    if ('y' in itemInfo && typeof itemInfo.y === 'number') (props as any).y = itemInfo.y
    if ('z' in itemInfo && typeof itemInfo.z === 'number') (props as any).z = itemInfo.z
    // IMPORTANTE: Asegurar que height siempre use alto del backend del nuevo item,
    // no la altura del item de referencia que podría venir en itemInfo
    if ('height' in itemInfo) {
      // Ignorar height de itemInfo; mantener height0 (LC23060 −100; laterales/LB8060: catálogo; RB +100).
      ;(props as any).height = height0
    }
  }

  return props satisfies Partial<Hole | Item | LinkedItem>
}
